import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, LoaderCircle, PencilLine, Plus, Trash2 } from 'lucide-react';
import {
  createProjectSkillApi,
  deleteProjectSkillApi,
  listProjectSkillsApi,
  updateProjectSkillApi
} from '../../api/projectSkills.api';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useI18n } from '../../hooks/useI18n';
import { hasProjectFeature } from '../../utils/projectFeatures';
import { ProjectSkillModal } from './ProjectSkillModal';

export const ProjectSkillsPanel = ({ project, onActivateFeature }) => {
  const { t } = useI18n();
  const projectId = project?.id;
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [activeSkill, setActiveSkill] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const databaseEnabled = hasProjectFeature(project, 'database');
  const authEnabled = hasProjectFeature(project, 'auth');

  const loadSkills = useCallback(async () => {
    if (!projectId) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const items = await listProjectSkillsApi(projectId);
      setSkills(items);
    } catch (loadError) {
      setSkills([]);
      setError(loadError?.message || t('skillsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  const openCreate = () => {
    setActiveSkill(null);
    setFormError('');
    setEditorOpen(true);
  };

  const openEdit = (skill) => {
    setActiveSkill(skill);
    setFormError('');
    setEditorOpen(true);
  };

  const handleSave = async (payload) => {
    if (!projectId || saving) {
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      if (activeSkill?.id) {
        await updateProjectSkillApi(projectId, activeSkill.id, payload);
      } else {
        await createProjectSkillApi(projectId, payload);
      }

      setEditorOpen(false);
      setActiveSkill(null);
      await loadSkills();
    } catch (saveError) {
      setFormError(saveError?.message || t('skillsSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!projectId || !deleteTarget?.id || deleting) {
      return;
    }

    setDeleting(true);

    try {
      await deleteProjectSkillApi(projectId, deleteTarget.id);
      setDeleteTarget(null);
      await loadSkills();
    } catch (deleteError) {
      setError(deleteError?.message || t('skillsDeleteError'));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="min-w-0 max-w-full space-y-4">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 max-w-2xl space-y-1">
          <h1 className="text-lg font-semibold text-neutral-950 dark:text-white">{t('skillsTitle')}</h1>
          <p className="text-sm leading-6 text-neutral-500">{t('skillsSubtitle')}</p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus size={16} />
          {t('skillsAdd')}
        </Button>
      </div>

      {!databaseEnabled || !authEnabled ? (
        <div className="flex flex-wrap gap-2">
          {!databaseEnabled ? (
            <Button type="button" variant="secondary" onClick={() => onActivateFeature?.('database')}>
              {t('featuresActivateDatabaseCta')}
            </Button>
          ) : null}
          {!authEnabled ? (
            <Button type="button" variant="secondary" onClick={() => onActivateFeature?.('auth')}>
              {t('featuresActivateAuthCta')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="flex min-h-[200px] items-center justify-center gap-2 text-sm text-neutral-500">
          <LoaderCircle className="animate-spin" size={18} />
          {t('skillsLoading')}
        </div>
      ) : error ? (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-10 text-center dark:border-rose-900/40 dark:bg-rose-900/20">
          <p className="text-sm text-rose-700 dark:text-rose-200">{error}</p>
          <Button type="button" variant="secondary" onClick={loadSkills}>
            {t('requestsRetry')}
          </Button>
        </div>
      ) : !skills.length ? (
        <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-neutral-200 px-6 py-14 text-center dark:border-neutral-800">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
            <BookOpen size={20} />
          </div>
          <div className="max-w-md space-y-2">
            <h2 className="text-sm font-semibold text-neutral-950 dark:text-white">{t('skillsEmptyTitle')}</h2>
            <p className="text-sm leading-6 text-neutral-500">{t('skillsEmptyBody')}</p>
          </div>
          <Button type="button" onClick={openCreate}>
            <Plus size={16} />
            {t('skillsAdd')}
          </Button>
        </div>
      ) : (
        <div className="grid min-w-0 gap-3">
          {skills.map((skill) => (
            <article
              key={skill.id}
              className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950"
            >
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <h2 className="min-w-0 break-words text-sm font-semibold text-neutral-950 dark:text-white">{skill.title}</h2>
                    <span className="rounded-full border border-neutral-200 px-2 py-0.5 text-[11px] font-medium text-neutral-500 dark:border-neutral-800">
                      {skill.isCustom ? t('skillsCustomBadge') : t('skillsDobbyBadge')}
                    </span>
                  </div>
                  {skill.description ? (
                    <p className="mt-1 break-words text-sm text-neutral-500">{skill.description}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800"
                    aria-label={t('skillsEdit')}
                    onClick={() => openEdit(skill)}
                  >
                    <PencilLine size={15} />
                  </button>
                  {skill.isCustom ? (
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-rose-500 transition hover:bg-rose-50 dark:hover:bg-rose-950/40"
                      aria-label={t('skillsDelete')}
                      onClick={() => setDeleteTarget(skill)}
                    >
                      <Trash2 size={15} />
                    </button>
                  ) : null}
                </div>
              </div>
              <pre className="mt-3 max-h-40 max-w-full overflow-x-hidden overflow-y-auto whitespace-pre-wrap break-words [overflow-wrap:anywhere] rounded-lg bg-neutral-50 p-3 text-xs leading-5 text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">
                {skill.bodyMarkdown}
              </pre>
            </article>
          ))}
        </div>
      )}

      <ProjectSkillModal
        open={editorOpen}
        skill={activeSkill}
        saving={saving}
        error={formError}
        onClose={() => {
          if (!saving) {
            setEditorOpen(false);
            setActiveSkill(null);
          }
        }}
        onSave={handleSave}
      />

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        closeDisabled={deleting}
        title={t('skillsDeleteTitle')}
      >
        <p className="text-sm leading-6 text-neutral-500">
          {t('skillsDeleteConfirm', { name: deleteTarget?.title || '' })}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" disabled={deleting} onClick={() => setDeleteTarget(null)}>
            {t('cancel')}
          </Button>
          <Button type="button" variant="danger" disabled={deleting} onClick={handleDelete}>
            {deleting ? t('skillsDeleting') : t('skillsDelete')}
          </Button>
        </div>
      </Modal>
    </section>
  );
};
